import React, { createContext, useContext, useMemo } from "react";
import {
  tableBodyStyle,
  tableCaptionStyle,
  tableDataStyle,
  tableFooterStyle,
  tableHeaderStyle,
  tableHeadStyle,
  tableRowStyleStyle,
  tableStyle,
} from "./styles";

type TableVariant = "bordered" | "plain";

const TableHeaderContext = createContext<{
  isHeaderRow: boolean;
}>({
  isHeaderRow: false,
});
const TableFooterContext = createContext<{
  isFooterRow: boolean;
}>({
  isFooterRow: false,
});
const TableVariantContext = createContext<{
  variant: TableVariant;
}>({
  variant: "bordered",
});

const Table = React.forwardRef<
  HTMLTableElement,
  React.ComponentProps<"table"> & { variant?: TableVariant }
>(function Table({ className, variant = "bordered", ...props }, ref) {
  const contextValue = useMemo(() => ({ variant }), [variant]);
  return (
    <TableVariantContext.Provider value={contextValue}>
      <table
        ref={ref}
        className={tableStyle({ class: className, variant })}
        {...props}
      />
    </TableVariantContext.Provider>
  );
});

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.ComponentProps<"thead">
>(function TableHeader({ className, ...props }, ref) {
  const contextValue = useMemo(() => {
    return {
      isHeaderRow: true,
    };
  }, []);
  return (
    <TableHeaderContext.Provider value={contextValue}>
      <thead
        ref={ref}
        className={tableHeaderStyle({ class: className })}
        {...props}
      />
    </TableHeaderContext.Provider>
  );
});

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.ComponentProps<"tbody">
>(function TableBody({ className, ...props }, ref) {
  return (
    <tbody
      ref={ref}
      className={tableBodyStyle({ class: className })}
      {...props}
    />
  );
});

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.ComponentProps<"tfoot">
>(function TableFooter({ className, ...props }, ref) {
  const contextValue = useMemo(() => {
    return {
      isFooterRow: true,
    };
  }, []);
  return (
    <TableFooterContext.Provider value={contextValue}>
      <tfoot
        ref={ref}
        className={tableFooterStyle({ class: className })}
        {...props}
      />
    </TableFooterContext.Provider>
  );
});

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ComponentProps<"th"> & { size?: "sm" | "md" | "lg" | "xl" }
>(function TableHead({ className, size = "md", ...props }, ref) {
  const { variant } = useContext(TableVariantContext);
  return (
    <th
      ref={ref}
      className={tableHeadStyle({ class: className, size, variant })}
      {...props}
    />
  );
});

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.ComponentProps<"tr">
>(function TableRow({ className, ...props }, ref) {
  const { isHeaderRow } = useContext(TableHeaderContext);
  const { isFooterRow } = useContext(TableFooterContext);
  const { variant } = useContext(TableVariantContext);
  return (
    <tr
      ref={ref}
      className={tableRowStyleStyle({
        variant,
        isHeaderRow,
        isFooterRow,
        class: className,
      })}
      {...props}
    />
  );
});

const TableData = React.forwardRef<
  HTMLTableCellElement,
  React.ComponentProps<"td">
>(function TableData(
  { className, size = "md", isLastRow, isSelected, colspan, ...props }: any,
  ref,
) {
  const { variant } = useContext(TableVariantContext);
  return (
    <td
      ref={ref}
      colSpan={colspan}
      className={tableDataStyle({
        class: className,
        size,
        isLastRow,
        isSelected,
        variant,
      })}
      {...props}
    />
  );
});

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.ComponentProps<"caption">
>(function TableCaption({ className, ...props }, ref) {
  return (
    <caption
      ref={ref}
      className={tableCaptionStyle({ class: className })}
      {...props}
    />
  );
});

Table.displayName = "Table";
TableHeader.displayName = "TableHeader";
TableBody.displayName = "TableBody";
TableFooter.displayName = "TableFooter";
TableHead.displayName = "TableHead";
TableRow.displayName = "TableRow";
TableData.displayName = "TableData";
TableCaption.displayName = "TableCaption";

export {
  Table,
  TableBody,
  TableCaption,
  TableData,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
};
